import type { User } from '@gm-commercial/profile-model';
import type {
  AdminEnrollmentRequest,
  MexicoFiscalDetails,
} from '@gm-commercial/rtk-clients/organizations/types';
import isEmpty from 'lodash/isEmpty';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import {
  type ApiAddressFields,
  mapAddressToApiResponse,
  mapApiResponseToAddress,
  stripPhoneNumber,
} from '~/common/mappers/common.mappers';
import type {
  PartialEntity,
  SummaryCounts,
} from '~/common/models/common.model';
import type {
  ApiAssetCountSummary,
  AssetCountSummary,
  Industry,
  Organization,
  OrganizationItem,
  OrgClassification,
} from '~/common/models/organization.model';
import { lowercaseLiteral } from '~/common/utils/common.utils';

import type { ValidOrgForm } from '../form/OrganizationForm.model';
import type { UpdateOrgFormPayload } from '../organizationsSlice';

type OrgApiRequestPayload = {
  name: string;
  onBehalfOfEmail: string;
  address: ApiAddressFields;
  country: string;
  industry: Industry;
  preferredName?: string;
  website?: string;
  fan?: string;
  primaryContact?: PartialEntity<User>;
  orgType?: OrgClassification;
  mexicoFiscalDetails?: MexicoFiscalDetails;
};

//TODO: Add assetCount and userCount in Phase 2 per API contract
type ApiOrgStats = {
  hubCount: number;
  fleetCount: number;
};
export const mapApiResponseToOrg = <
  Result extends Partial<
    Omit<Organization, 'address' | 'status' | 'primaryContact'>
  > & {
    address: ApiAddressFields | null;
    primaryContact: PartialEntity<User> | null;
  } & Record<string, unknown>,
>(
  rawResult: Result
): Organization => {
  const mapped = {
    ...rawResult,
    email: rawResult.emailAddress || '',
    phoneNumber:
      rawResult.phoneNumber && rawResult.phoneNumber.length >= 10
        ? stripPhoneNumber(rawResult.phoneNumber)
        : DEFAULT_EMPTY_VALUE,
    address: rawResult.address
      ? mapApiResponseToAddress(rawResult.address)
      : undefined,
    status:
      typeof rawResult.status === 'string'
        ? lowercaseLiteral(rawResult.status)
        : undefined,
    primaryContact: rawResult.primaryContact || undefined,
    orgType: rawResult.type || undefined,
    orgMasterNumber: rawResult.orgMasterNumber || undefined,
  };
  return mapped as Organization;
};

export const mapApiResponseToOrganization = <
  Result extends Partial<
    Omit<OrganizationItem, 'address' | 'status' | 'primaryContact'>
  > & {
    address: ApiAddressFields | null;
    primaryContact: PartialEntity<User> | null;
  } & Record<string, unknown>,
>(
  rawResult: Result
): OrganizationItem => {
  const mapped = {
    ...rawResult,
    emailAddress: rawResult.emailAddress || '',
    address: !isEmpty(rawResult.address) ? rawResult.address : undefined,
    status: typeof rawResult.status === 'string' ? rawResult.status : undefined,
    primaryContact: rawResult.primaryContact ?? null,
    terms: rawResult.terms || undefined,
  };
  return mapped as OrganizationItem;
};

/**
 * Maps the edit-organization form to the update request body. An empty
 * classification or absent fiscal details are omitted so they stay unchanged.
 */
export const mapOrgFormToApiRequest = (
  payload: UpdateOrgFormPayload
): OrgApiRequestPayload => {
  const {
    name,
    address,
    preferredName,
    onBehalfOfEmail,
    industry,
    website,
    fan,
    orgType,
    mexicoFiscalDetails,
  } = payload.formFields;
  let result = {
    name: name.trim(),
    address: mapAddressToApiResponse(address),
    onBehalfOfEmail,
    industry,
    preferredName,
    website,
    fan: fan ? fan : null,
    ...(orgType ? { orgType } : {}),
    ...(mexicoFiscalDetails ? { mexicoFiscalDetails } : {}),
  } as OrgApiRequestPayload;
  if (payload.params?.organizationsId && payload.formFields.primaryContact) {
    const { id, name } = payload.formFields.primaryContact;
    result = { ...result, primaryContact: { id, name } };
  }
  return result;
};

/**
 * Maps the add-organization form to the RTK enrollAsAdmin request body.
 */
export const mapOrgFormToAdminEnrollmentRequest = (
  formFields: ValidOrgForm
): AdminEnrollmentRequest => {
  const {
    name,
    address,
    preferredName,
    onBehalfOfEmail,
    industry,
    website,
    fan,
    taxId,
    countryOfOperation,
    mexicoFiscalDetails,
  } = formFields;
  const mappedAddress = mapAddressToApiResponse(address);

  return {
    name: name.trim(),
    onBehalfOfEmail,
    industry,
    address: {
      line1: mappedAddress.line1,
      line2: mappedAddress.line2,
      city: mappedAddress.city,
      state: mappedAddress.state ?? '',
      postalCode: mappedAddress.postalCode,
      country: mappedAddress.country,
    },
    ...(preferredName ? { preferredName } : {}),
    ...(website ? { website } : {}),
    ...(fan ? { fan } : {}),
    ...(taxId ? { taxId } : {}),
    ...(countryOfOperation ? { countryOfOperation } : {}),
    ...(countryOfOperation === 'MX' && mexicoFiscalDetails
      ? { mexicoFiscalDetails }
      : {}),
    orgType: formFields.orgType ?? 'CUSTOMER',
  };
};

//TODO: Phase 2 will add assets and users
export const mapStatsApiResponseToSummaryCounts = (
  apiOrgStats: ApiOrgStats
): SummaryCounts =>
  ({
    ...apiOrgStats,
    hubs: apiOrgStats.hubCount,
    fleets: apiOrgStats.fleetCount,
  }) as SummaryCounts;

export const mapApiToAssetCountSummary = (
  summary: Partial<ApiAssetCountSummary>
): AssetCountSummary => {
  const { fleets } = summary;
  return {
    organizationId: summary.organizationId,
    numberOfAssets: summary.numberOfAssets,
    fleets,
  } as AssetCountSummary;
};
